<template>
  <on-click-outside :do="close">
    <div class="search-select" :class="{ 'is-active': isOpen }">
      <button
        ref="button"
        @click="open"
        type="button"
        class="search-select-input"
        :class="[{ 'search-select-input__disabled': disabled }]"
        :disabled="disabled"
      >
        <span v-if="option !== null" class="search-select-text">
          {{ typeof option === 'object' ? option.text : option }}
        </span>
        <span v-else class="search-select-placeholder">--Select--</span>
        <span
          class="search-select__arrow"
          :class="{ 'search-select__arrow__open': isOpen }"
        ></span>
      </button>
      <div ref="dropdown" v-show="isOpen" class="search-select-dropdown">
        <input
          class="search-select-search"
          v-model="search"
          ref="search"
          @keydown.esc="close"
          @keydown.up="highlightPrev"
          @keydown.down="highlightNext"
          @keydown.enter.prevent="selectHighlighted"
          @input="searchText"
        />
        <ul
          ref="options"
          v-show="filteredOptions.length > 0"
          class="search-select-options"
        >
          <li
            class="search-select-option"
            v-for="(option, i) in filteredOptions"
            :key="typeof option === 'object' ? option.value : option"
            @click="select(option)"
            :class="{ 'is-active': i === highlightedIndex }"
          >
            {{ typeof option === 'object' ? option.text : option }}
          </li>
        </ul>
        <div v-show="filteredOptions.length === 0" class="search-select-empty">
          <slot name="no-result" :search="search">
            No results found for
            <b>" {{ search }}"</b>
          </slot>
        </div>
      </div>
    </div>
  </on-click-outside>
</template>

<script>
import OnClickOutside from '@/components/OnClickOutside'
import Popper from 'popper.js'

export default {
  components: {
    OnClickOutside
  },
  props: ['value', 'options', 'filterFunction', 'disabled'],
  data() {
    return {
      isOpen: false,
      search: '',
      highlightedIndex: null,
      option:
        this.options.find(
          option =>
            this.value == (typeof option === 'object' ? option.value : option)
        ) || null
    }
  },
  beforeDestroy() {
    this.popper !== undefined && this.popper.destroy()
  },
  computed: {
    filteredOptions() {
      if (this.filterFunction !== undefined) {
        return this.filterFunction(this.search, this.options)
      }

      return this.options.filter(option => {
        option = typeof option === 'object' ? option.text : option

        return option.toLowerCase().startsWith(this.search.toLowerCase())
      })
    }
  },
  methods: {
    open() {
      if (!this.isOpen) {
        this.isOpen = true
        this.$nextTick(() => {
          this.setupPopper()
          this.$refs.search.focus()
          this.scrollToHighlighted()
        })
      } else {
        this.isOpen = false
      }
    },
    setupPopper() {
      if (this.popper === undefined) {
        this.popper = new Popper(this.$refs.button, this.$refs.dropdown, {
          placement: 'bottom',
          positionFixed: true,
          modifiers: {
            offset: {
              enabled: true,
              fn: data => {
                data.offsets.popper.left = data.offsets.reference.left
                data.offsets.popper.right = data.offsets.reference.right
                data.offsets.popper.width = data.styles.width = Math.round(
                  data.offsets.reference.width
                )
                return data
              },
              order: 840
            }
          }
        })
      } else {
        this.popper.scheduleUpdate()
      }
    },
    close() {
      if (!this.isOpen) {
        return
      }
      this.isOpen = false
      this.$refs.button.focus()
    },
    select(option) {
      this.$emit('input', typeof option === 'object' ? option.value : option)
      this.$emit('change', typeof option === 'object' ? option.value : option)
      this.search = ''
      this.option = option
      this.highlightedIndex = this.filteredOptions.indexOf(option)
      this.close()
    },
    selectHighlighted() {
      this.select(this.filteredOptions[this.highlightedIndex])
    },
    scrollToHighlighted() {
      if (this.highlightedIndex === null) return

      this.$refs.options.children.length > 0 &&
        this.$refs.options.children[this.highlightedIndex].scrollIntoView({
          block: 'nearest'
        })
    },
    highlight(index) {
      this.highlightedIndex = index

      if (this.highlightedIndex < 0) {
        this.highlightedIndex = this.filteredOptions.length - 1
      }

      if (this.highlightedIndex > this.filteredOptions.length - 1) {
        this.highlightedIndex = 0
      }

      this.scrollToHighlighted()
    },
    highlightPrev() {
      this.highlight(this.highlightedIndex - 1)
    },
    highlightNext() {
      this.highlight(this.highlightedIndex + 1)
    },
    searchText() {
      this.$nextTick(() => {
        this.popper.scheduleUpdate()
      })
    }
  },
  watch: {
    value(val) {
      this.option =
        this.filteredOptions.find(
          option => val == (typeof option === 'object' ? option.value : option)
        ) || null
    }
  }
}
</script>

<style lang="scss">
.search-select {
  position: relative;
}
.search-select-input {
  appearance: none;
  text-align: left;
  cursor: pointer;
  user-select: none;
  display: block;
  width: 100%;
  height: 34px;
  padding: 6px 12px;
  font-size: 12px;
  line-height: 1.42857143;
  color: #555;
  background-color: #fff;
  background-image: none;
  border: 1px solid #ccc;
  box-shadow: none;
  border-color: #d2d6de;
  white-space: nowrap;
  display: flex;
  align-items: center;
  border-radius: 0.25rem;

  &:focus {
    border-color: #3c8dbc;
  }

  &__disabled {
    background-color: #e5e9ed;
    opacity: 0.6;
  }
}
.search-select-placeholder,
.search-select-text {
  flex-grow: 1;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
}
.search-select__arrow {
  transition: all 200ms ease-in-out;

  &::before {
    display: block;
    color: #999;
    border-style: solid;
    border-width: 5px 5px 0;
    border-color: #999 transparent transparent;
    content: '';
  }

  &__open {
    transform: rotate(180deg);
  }
}
.search-select-dropdown {
  margin-top: -0.3rem;
  margin-bottom: -0.3rem;
  position: absolute;
  border-top: none !important;
  border-bottom: none !important;
  right: 0;
  left: 0;
  margin-left: 1px;
  width: 100%;
  background-color: #fff;
  border-radius: 0.25rem;
  // box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
  z-index: 99997;
  border: solid 1px #d2d6de;
  display: flex;
  flex-direction: column;
}
.search-select-search {
  display: block;
  padding: 0.5rem 0.75rem;
  background-color: #ededed;
  border: 1px solid #ccc;
  margin: 0.5rem;

  // &:focus {
  //   border-color: #3c8dbc;
  // }
}
.search-select-options {
  list-style: none;
  padding: 0;
  position: relative;
  overflow-y: scroll;
  max-height: 15rem;
  margin: 0;
}
.search-select-option {
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  user-select: none;

  &:hover {
    background-color: #e9e9e9;
    color: rgb(94, 94, 94);
  }

  &.is-active {
    background-color: #269add;
    color: #fff;
  }
}
.search-select-empty {
  text-align: center;
  padding: 0.5rem;
}
::-webkit-scrollbar {
  border-radius: 50px;
  width: 3px; /* Remove scrollbar space */
  background: transparent; /* Optional: just make scrollbar invisible */
}
/* Optional: show position indicator in red */
::-webkit-scrollbar-thumb {
  background: rgb(194, 194, 194);
}
</style>
